import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Button, Card, Spinner, Alert, AlertDescription, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  SearchInput, EmptyState,
} from '@agion/ui'
import { HardDrive, Plus, RefreshCw, Package } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatDate, aptChannelFromVersion, channelForEnvironment } from '@/core/infra_utils'
import { getConfig } from '@/core/infra_runtime_env'
import {
  listImages, listDeviations, listRegistryImages, refreshDeviations,
  type PackerImage, type ImageDeviation, type RegistryOverview,
} from './infra_image.service'
import { ImageStatusBadge } from './InfraImageStatusBadge'
import { DeviationsBanner } from './InfraDeviationsBanner'
import { BuildImageDialog } from './InfraBuildImageDialog'

/** Registry shows Harbor images with build status, Builds shows DB records */
type ViewMode = 'registry' | 'builds'

export function ImagesPage() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('registry')
  const [registryImages, setRegistryImages] = useState<RegistryOverview[]>([])
  const [images, setImages] = useState<PackerImage[]>([])
  const [deviations, setDeviations] = useState<ImageDeviation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [providerFilter, setProviderFilter] = useState('')
  const [buildTarget, setBuildTarget] = useState<RegistryOverview | null>(null)

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const [registryResp, imagesResp, deviationsResp] = await Promise.all([
        listRegistryImages().catch(() => [] as RegistryOverview[]),
        listImages({
          status: statusFilter || undefined,
          cloudProvider: providerFilter || undefined,
        }),
        listDeviations(),
      ])
      setRegistryImages(registryResp ?? [])
      setImages(imagesResp.data ?? [])
      setDeviations(deviationsResp.data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images')
    } finally {
      setIsLoading(false)
    }
  }, [statusFilter, providerFilter])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const handleRefreshDeviations = async () => {
    setIsRefreshing(true)
    try {
      await refreshDeviations()
      await loadData()
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleBuildSuccess = (operationId: string) => {
    void loadData()
    navigate(`/operations/${operationId}`)
  }

  // Environment parity: only show images matching this env's APT channel
  const envChannel = channelForEnvironment(getConfig('VITE_ENVIRONMENT'))

  // Filter registry images
  const filteredRegistry = registryImages.filter((ri) => {
    if (envChannel && aptChannelFromVersion(ri.version) !== envChannel) return false
    if (providerFilter && ri.cloud !== providerFilter) return false
    if (statusFilter) {
      if (statusFilter === 'available' && ri.build?.status !== 'available') return false
      if (statusFilter === 'building' && ri.build?.status !== 'building') return false
      if (statusFilter === 'failed' && ri.build?.status !== 'failed') return false
      if (statusFilter === 'deprecated' && ri.build?.status !== 'deprecated') return false
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      return ri.version.toLowerCase().includes(q) || ri.cloud.toLowerCase().includes(q)
    }
    return true
  })

  // Filter build images
  const filteredBuilds = images.filter((img) => {
    if (envChannel && aptChannelFromVersion(img.version) !== envChannel) return false
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      img.name.toLowerCase().includes(q) ||
      img.cloudProvider.toLowerCase().includes(q) ||
      img.region.toLowerCase().includes(q) ||
      img.version.toLowerCase().includes(q) ||
      img.templateId.toLowerCase().includes(q)
    )
  })

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }

  const cloudLabel = (cloud: string) => {
    const labels: Record<string, string> = { upcloud: 'UpCloud', gcp: 'GCP', azure: 'Azure', vmware: 'VMware' }
    return labels[cloud] ?? cloud
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('pages:images.title', 'Packer Images')}
        description={t('pages:images.description', 'Manage machine images across cloud providers')}
        icon={HardDrive}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void loadData()}>
              <RefreshCw size={14} className="mr-1" />
              {t('pages:images.refresh', 'Refresh')}
            </Button>
            {/* Build button removed — use "Build" on individual registry rows */}
          </>
        }
      />

      <DeviationsBanner
        deviations={deviations}
        onRefresh={() => void handleRefreshDeviations()}
        isRefreshing={isRefreshing}
      />

      <div className="flex items-center gap-4">
        <div className="max-w-md flex-1">
          <SearchInput
            placeholder={t('pages:images.search', 'Search images...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
          />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('pages:images.filters.allStatuses', 'All Statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:images.filters.allStatuses', 'All Statuses')}</SelectItem>
            <SelectItem value="available">{t('pages:images.filters.available', 'Available')}</SelectItem>
            <SelectItem value="building">{t('pages:images.filters.building', 'Building')}</SelectItem>
            <SelectItem value="failed">{t('pages:images.filters.failed', 'Failed')}</SelectItem>
            <SelectItem value="deprecated">{t('pages:images.filters.deprecated', 'Deprecated')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={providerFilter || 'all'} onValueChange={(v) => setProviderFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('pages:images.filters.allProviders', 'All Providers')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:images.filters.allProviders', 'All Providers')}</SelectItem>
            <SelectItem value="upcloud">{t('pages:providers.upcloud')}</SelectItem>
            <SelectItem value="gcp">{t('pages:providers.gcpShort')}</SelectItem>
            <SelectItem value="azure">{t('pages:providers.azure')}</SelectItem>
            <SelectItem value="vmware">{t('pages:providers.vmware')}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1 ml-auto">
          <Button
            variant={viewMode === 'registry' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setViewMode('registry')}
          >
            <Package size={14} className="mr-1" />
            {t('pages:images.views.registry')}
          </Button>
          <Button
            variant={viewMode === 'builds' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setViewMode('builds')}
          >
            <HardDrive size={14} className="mr-1" />
            {t('pages:images.views.builds')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : viewMode === 'registry' ? (
        <RegistryView
          items={filteredRegistry}
          cloudLabel={cloudLabel}
          formatSize={formatSize}
          onBuild={(img) => setBuildTarget(img)}
          navigate={navigate}
        />
      ) : (
        <BuildsView
          items={filteredBuilds}
          searchQuery={searchQuery}
          statusFilter={statusFilter}
          providerFilter={providerFilter}
          onBuild={() => {}}
          navigate={navigate}
        />
      )}

      <BuildImageDialog
        open={buildTarget !== null}
        image={buildTarget}
        onClose={() => setBuildTarget(null)}
        onSuccess={handleBuildSuccess}
      />
    </div>
  )
}

function RegistryView({
  items,
  cloudLabel,
  formatSize,
  onBuild,
  navigate,
}: {
  items: RegistryOverview[]
  cloudLabel: (c: string) => string
  formatSize: (b: number) => string
  onBuild: (image: RegistryOverview) => void
  navigate: (path: string) => void
}) {
  const { t } = useTranslation(['pages'])
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Package}
          title={t('pages:images.registry.emptyTitle')}
          description={t('pages:images.registry.emptyDescription')}
        />
      </Card>
    )
  }

  return (
    <Table variant="modern">
      <TableHeader variant="modern">
        <TableRow variant="modern">
          <TableHead variant="modern">{t('pages:images.columns.version')}</TableHead>
          <TableHead variant="modern">{t('pages:images.buildDialog.cloud')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.size', 'Size')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.pushed', 'Pushed')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.buildStatus', 'Build Status')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.actions', 'Actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody variant="modern">
        {items.map((ri) => (
          <TableRow key={`${ri.cloud}-${ri.version}`} variant="modern">
            <TableCell className="font-mono font-medium">{ri.version}</TableCell>
            <TableCell>
              <Badge variant="default">{cloudLabel(ri.cloud)}</Badge>
            </TableCell>
            <TableCell className="text-slate-500">{formatSize(ri.sizeBytes)}</TableCell>
            <TableCell className="text-slate-500">{formatDate(ri.pushedAt)}</TableCell>
            <TableCell>
              {ri.build ? (
                <ImageStatusBadge status={ri.build.status as 'available' | 'building' | 'failed' | 'deprecated'} />
              ) : (
                <Badge variant="default">{t('pages:images.status.notBuilt')}</Badge>
              )}
            </TableCell>
            <TableCell>
              <div className="flex gap-1">
                {ri.build ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/images/${ri.build!.imageId}`)}
                    >
                      {t('pages:images.actions.details', 'Details')}
                    </Button>
                    {ri.build.status === 'failed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onBuild(ri)}
                      >
                        {t('pages:images.actions.retry', 'Retry')}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onBuild(ri)}
                  >
                    {t('pages:images.buildDialog.build')}
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function BuildsView({
  items,
  searchQuery,
  statusFilter,
  providerFilter,
  onBuild,
  navigate,
}: {
  items: PackerImage[]
  searchQuery: string
  statusFilter: string
  providerFilter: string
  onBuild: () => void
  navigate: (path: string) => void
}) {
  const { t } = useTranslation(['pages'])
  if (items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={HardDrive}
          title={t('pages:images.empty.title', 'No images found')}
          description={
            searchQuery || statusFilter || providerFilter
              ? t('pages:images.empty.searchDescription', 'No images match your search criteria.')
              : t('pages:images.empty.description', 'Build your first Packer image to get started.')
          }
          action={
            !searchQuery && !statusFilter && !providerFilter ? (
              <Button variant="primary" size="sm" onClick={onBuild}>
                <Plus size={14} className="mr-1" />
                {t('pages:images.build', 'Build Image')}
              </Button>
            ) : undefined
          }
        />
      </Card>
    )
  }

  return (
    <Table variant="modern">
      <TableHeader variant="modern">
        <TableRow variant="modern">
          <TableHead variant="modern">{t('pages:images.columns.name', 'Name')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.template', 'Template')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.version', 'Version')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.provider', 'Provider')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.region', 'Region')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.status', 'Status')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.created', 'Created')}</TableHead>
          <TableHead variant="modern">{t('pages:images.columns.actions', 'Actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody variant="modern">
        {items.map((img) => (
          <TableRow key={img.id} variant="modern">
            <TableCell className="font-medium">{img.name}</TableCell>
            <TableCell className="text-slate-500">{img.templateId}</TableCell>
            <TableCell>{img.version}</TableCell>
            <TableCell>{img.cloudProvider}</TableCell>
            <TableCell>{img.region}</TableCell>
            <TableCell>
              <ImageStatusBadge status={img.status} />
            </TableCell>
            <TableCell className="text-slate-500">{formatDate(img.createdAt)}</TableCell>
            <TableCell>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/images/${img.id}`)}
              >
                {t('pages:images.actions.details', 'Details')}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
